import type { UIConfigVariable, UISetupConfig, Dependency } from 'types/ui-setup-config';
import type { SystemComponent, ComponentInstance } from 'types/component';
import { isEmpty } from '../../utils/helper/helper';
import {
  deriveInactiveCalcValues,
  getDerivableInactiveCalcVariables,
} from '../../utils/helper/calculateInactiveCalcHelper/deriveInactiveCalcValues';
import type { UnitConversion } from '../../utils/helper/calculateInactiveCalcHelper/calculateInactiveInput';

export const readVariableValue = <T = unknown>(
  values: Record<string, any> | undefined,
  element: string | undefined,
  name: string,
): T | undefined => {
  if (!values) return undefined;
  return (element ? values?.[element]?.[name] : values?.[name]) as T | undefined;
};

// Mutates `target` on purpose so callers can build a values object in place.
/* eslint-disable no-param-reassign */
export const writeVariableValue = (
  target: Record<string, any>,
  element: string | undefined,
  name: string,
  value: unknown,
): void => {
  if (!element) {
    target[name] = value;
    return;
  }
  if (!target[element] || typeof target[element] !== 'object') target[element] = {};
  target[element][name] = value;
};
/* eslint-enable no-param-reassign */

export const getFormikFieldValue = (
  values: Record<string, any>,
  variable?: { name: string; element?: string },
): unknown => (variable ? readVariableValue(values, variable.element, variable.name) : null);

// Clearing a nested Formik error leaves its parent behind
export const hasFormErrors = (errors: unknown): boolean =>
  errors !== null && typeof errors === 'object' ? Object.values(errors).some(hasFormErrors) : !!errors;

// Formik splits names on `.` and `[ ]`, so a segment containing them is quoted to stay one key.
// Inside the quotes `'` and `\` are special to lodash, so they get a backslash.
const formikPathKey = (key: string): string =>
  /[.[\]]/.test(key) ? `['${key.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}']` : key;

export const variableFieldPath = (element: string | undefined, name: string): string =>
  element ? `${formikPathKey(element)}.${formikPathKey(name)}` : formikPathKey(name);

export const componentParameterName = (fmuComponentName: string | undefined, parameterName: string): string =>
  `${fmuComponentName}.${parameterName}`;

// Safe as long as element and variable names never contain '::'.
const PARAM_KEY_SEP = '::';
export const paramKey = (element: string | undefined, name: string | undefined): string =>
  `${element ?? ''}${PARAM_KEY_SEP}${name ?? ''}`;
export const parseParamKey = (key: string): { element: string | undefined; name: string } => {
  const i = key.indexOf(PARAM_KEY_SEP);
  if (i < 0) return { element: undefined, name: key };
  const element = key.slice(0, i) || undefined;
  return { element, name: key.slice(i + PARAM_KEY_SEP.length) };
};

export const formatVariableLabel = (v: { name?: string; element?: string }): string => {
  const name = v.name ?? '';
  return v.element ? `${name} [${v.element}]` : name;
};

export const normalizeCalcRef = (raw: unknown): { name: string; element?: string } =>
  typeof raw === 'string' ? { name: raw } : (raw as { name: string; element?: string });

// ArrayField seeds new rows with a flat dotted key (`'xVariable.name'`) while the Select
// writes the nested path, so the stale flat keys are dropped before saving.
export const stripDottedKeys = <T>(node: T): T => {
  if (Array.isArray(node)) return (node as any).map(stripDottedKeys) as T;
  if (!node || typeof node !== 'object') return node;
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(node as Record<string, any>)) {
    if (!k.includes('.')) out[k] = stripDottedKeys(v);
  }
  return out as T;
};

export const getVariablesOfSubMenu = (
  uiSetupConfig: UISetupConfig,
  lev1: string,
  lev2: string,
): Array<{
  alias: string;
  name: string;
  element?: string;
  inputType: string;
  unit: string;
  dependency: Dependency;
  hidden?: boolean;
}> => {
  const discardedVariable = {
    alias: 'Discarded Variable',
    name: 'discarded_varibale',
    unit: '-',
    inputType: 'number',
    dependency: {},
  };
  if (isEmpty(uiSetupConfig)) {
    return [discardedVariable];
  }

  const le1 = uiSetupConfig.find((el) => el.name === lev1);
  const subMenu = le1
    ? le1.subMenu
    : [
        {
          name: 'SubMenu',
          variables: [
            {
              alias: 'Variable',
              name: 'Variable',
              inputType: 'number',
              unit: '-',
            },
          ],
        },
      ];

  const le2 = subMenu?.find((el) => el.name === lev2);
  return le2
    ? le2.variables.map((variable: any) => ({
        alias: variable.alias ?? '',
        name: variable.name,
        element: variable.element,
        inputType: variable.inputType,
        unit: variable.unit ?? '',
        dependency: {},
        hidden: false,
      }))
    : [discardedVariable];
};

export const getVariableUnit = (variableName: string, variables: Array<Partial<UIConfigVariable>>) => {
  if (isEmpty(variables)) {
    return '-';
  }

  const variable = variables.find((el) => el.name === variableName || el.alias === variableName);

  if (variable) {
    let unit = variable.displayUnit && variable.displayUnit !== 'null' ? variable.displayUnit : variable.unit;
    unit = !unit || unit === '1' ? '-' : unit;
    return unit;
  }
  return '-';
};

export const buildDependencyPayload = (dependency: Partial<Dependency> | undefined): Dependency | {} => {
  const { name, value, type, ignoreIfHidden } = dependency ?? {};
  if (!name || value === undefined || value === '') return {};
  return {
    name,
    type: type || 'showValue',
    value,
    ignoreIfHidden,
  };
};

// Dependencies don't carry an element, so the first element holding this name wins.
const findDependencyValue = <T = unknown>(values: Record<string, any>, name: string): T | undefined => {
  if (name in values) return values[name] as T;
  for (const v of Object.values(values)) {
    if (v && typeof v === 'object' && !Array.isArray(v) && name in v) {
      return (v as Record<string, unknown>)[name] as T;
    }
  }
  return undefined;
};

export const checkVariableDependency = (
  values: Record<string, any>,
  dependency?: { name?: string; type?: string; value?: any },
) => {
  if (dependency) {
    const targetName = dependency?.name ?? '';
    const targetValue = findDependencyValue(values, targetName);
    if (dependency.type === 'hideValue') {
      if (targetValue?.toString() === dependency.value?.toString()) return false;
    }

    if (dependency.type === 'showValue') {
      if (targetValue?.toString() !== dependency.value?.toString()) return false;
    }
  }
  return true;
};

export const getMappedFmuUiValues = (
  values: Record<string, any>,
  uiVariablesList: Array<Partial<UIConfigVariable>>,
) => {
  const mappedFmuInputs: Record<string, string | number | boolean> = {};
  const fmuInputsList = uiVariablesList.filter((el) => el.uiType === 'fmuInput');
  fmuInputsList.forEach((input) => {
    if (!input.name) return;
    const value = readVariableValue<string | number | boolean>(values, input.element, input.name);
    if (value === undefined) return;
    // Emit dot-notation flat keys so the backend treats them as element-scoped.
    const key = input.element ? `${input.element}.${input.name}` : input.name;
    mappedFmuInputs[key] = value;
  });

  return {
    inputsFMU: mappedFmuInputs,
  };
};

const TYPE_TO_DEFAULT_INPUT_TYPE: Record<string, string> = {
  Real: 'ButtonField',
  Integer: 'ButtonField',
  Number: 'ButtonField',
  Boolean: 'Checkbox',
  String: 'TextField',
  Array: 'InputTable',
  Table: 'InputTable',
  Enumeration: 'SelectField',
};

export const getVariableList = (
  uiSetupConfigComplete: UISetupConfig,
  systemComponents: Array<SystemComponent> = [],
) => {
  const variableList = uiSetupConfigComplete
    .flatMap((menu) => menu.subMenu)
    .flatMap((subMenu) =>
      subMenu?.variables.flatMap((variable) => {
        if (variable.inputType === 'ComponentSelection') {
          const systemComponent = systemComponents?.find((c) => c.name === variable.fmuComponentName);
          const parameterDefinitions = systemComponent?.component?.merged_parameter_definitions ?? {};
          // Sub-parameters always take the parent's element, even if their definition has one.
          const subParams = Object.entries(parameterDefinitions).map(([parameterName, def]: [string, any]) => ({
            ...def,
            inputType: def?.inputType ?? TYPE_TO_DEFAULT_INPUT_TYPE[def?.type as string],
            name: componentParameterName(variable.fmuComponentName, parameterName),
            element: variable.element,
          }));
          // Push the parent variable LAST so its initial value wins over the parameter defaults.
          return [...subParams, variable];
        }

        return [variable];
      }),
    );

  return variableList;
};

export const getInitialValues = (variableList: UIConfigVariable[], systemComponents: Array<any>) => {
  const initValues: Record<string, any> = {};

  const writeAt = (element: string | undefined, key: string, value: any) => {
    if (element) {
      if (!initValues[element] || typeof initValues[element] !== 'object') {
        initValues[element] = {};
      }
      initValues[element][key] = value;
    } else {
      initValues[key] = value;
    }
  };

  variableList.forEach((variable: UIConfigVariable) => {
    const { element } = variable;

    if (variable.inputType === 'variableInput') {
      if (variable.variableInput?.start) {
        const startValues: Array<Record<string, any>> = [];
        variable.variableInput.start.forEach((el, indStartValues) => {
          const rowValues: Record<string, any> = { id: indStartValues };
          variable.variableInput?.columnNames?.forEach((name, indColumnName) => {
            rowValues[name] = el[indColumnName];
          });
          startValues.push(rowValues);
        });
        writeAt(element, variable.name, startValues);
      }
      return;
    }

    if (['Table', 'InputTable'].includes(variable.inputType)) {
      writeAt(element, variable.name, variable.arrays?.start);
      writeAt(element, `${variable.name}_dataSource`, variable.dataSource ?? '');
      return;
    }

    if (variable.inputType === 'ComponentSelection') {
      const systemComponent = systemComponents?.find((c) => c.name === variable.fmuComponentName);
      const defaultInstance = systemComponent?.default_instance || systemComponent?.instances[0]?.id;
      writeAt(element, variable.name, defaultInstance);
      if (systemComponent) {
        const params = systemComponent.instances.find((i: ComponentInstance) => i.id === defaultInstance)?.parameters;
        for (const [key, value] of Object.entries(params ?? {})) {
          writeAt(element, componentParameterName(variable.fmuComponentName, key), value);
        }
      }
      return;
    }

    // InactiveCalc values are seeded below, once every input default is in place.
    if (variable.inputType === 'InactiveCalc') return;

    if (variable.inputType !== 'Header') {
      writeAt(element, variable.name, variable.start);
    }
  });

  // Seed for the results page of older simulations; live forms derive these values.
  // Start values are in base units, so no conversion applies.
  const calcVariables = getDerivableInactiveCalcVariables(variableList);
  if (calcVariables.length > 0) {
    const identity: UnitConversion = (_from, value) => value;
    const derived = deriveInactiveCalcValues(initValues, calcVariables, variableList, identity);
    calcVariables.forEach((variable) => {
      const value = derived.baseValues.get(paramKey(variable.element, variable.name));
      if (value !== undefined) writeAt(variable.element, variable.name, value);
    });
  }

  return initValues;
};
export const transformVariableObjectToArray = <T extends object = Record<string, unknown>>(
  variablesData: Record<string, any> | null | undefined,
): Array<T & { element?: string; name?: string }> =>
  Object.entries(variablesData ?? {}).flatMap(([element, group]): Array<T & { element?: string; name?: string }> => {
    // Unscoped variables can sit next to element groups; a variable has `name`, a group doesn't.
    if (!group || typeof group !== 'object' || Array.isArray(group) || 'name' in (group as object)) {
      return [{ ...(group as T), element: undefined, name: element }];
    }
    return Object.values(group as Record<string, T>).map((variable) => ({ ...variable, element }));
  });

export default getVariablesOfSubMenu;
